import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE, useAuth } from '../../context/AuthContext';
import { useAdmin } from '../../context/AdminContext';
import { formatCount, formatDate } from '../../lib/admin-utils';
import type { LinkItem } from '../../types/admin';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, BarChart3, Copy, ExternalLink, ArrowRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

type WindowPreset = '24h' | '7d' | '30d' | '90d';

interface RecentLinksResponse {
  links?: LinkItem[];
}

function presetFromAnalytics(days: number, granularity: 'hour' | 'day'): WindowPreset {
  if (days <= 1 && granularity === 'hour') return '24h';
  if (days <= 7 && granularity === 'hour') return '7d';
  if (days <= 30) return '30d';
  return '90d';
}

export default function OverviewPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const {
    selectedTenant,
    analytics,
    analyticsQuery,
    refreshAnalytics,
    setNotice,
    makeShortUrl,
  } = useAdmin();

  const WINDOW_PRESETS: Record<WindowPreset, { days: number; granularity: 'hour' | 'day'; label: string }> = {
    '24h': { days: 1, granularity: 'hour', label: t('overview.window24h') },
    '7d': { days: 7, granularity: 'hour', label: t('overview.window7d') },
    '30d': { days: 30, granularity: 'day', label: t('overview.window30d') },
    '90d': { days: 90, granularity: 'day', label: t('overview.window90d') },
  };

  const [windowPreset, setWindowPreset] = useState<WindowPreset>('30d');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [recentLinks, setRecentLinks] = useState<LinkItem[]>([]);
  const [recentLinksLoading, setRecentLinksLoading] = useState(false);

  useEffect(() => {
    setWindowPreset(presetFromAnalytics(analyticsQuery.days, analyticsQuery.granularity));
  }, [analyticsQuery.days, analyticsQuery.granularity]);

  useEffect(() => {
    if (!selectedTenant?.tenantId || analytics || analyticsLoading) return;
    const preset = WINDOW_PRESETS[windowPreset];
    let active = true;
    (async () => {
      try {
        setAnalyticsLoading(true);
        await refreshAnalytics({ days: preset.days, granularity: preset.granularity, urlCode: null });
      } catch {
        /* ignore initial load errors */
      } finally {
        if (active) setAnalyticsLoading(false);
      }
    })();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant?.tenantId]);

  useEffect(() => {
    if (!selectedTenant?.tenantId || !token) { setRecentLinks([]); return; }
    const controller = new AbortController();
    let active = true;
    const loadRecentLinks = async () => {
      try {
        setRecentLinksLoading(true);
        const params = new URLSearchParams();
        params.set('tenantId', selectedTenant.tenantId);
        params.set('paginated', '1');
        params.set('limit', '6');
        params.set('sort', 'recent');
        const response = await fetch(`${API_BASE}/links?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Failed to load recent links');
        const data = await response.json() as RecentLinksResponse;
        if (active) setRecentLinks(Array.isArray(data.links) ? data.links : []);
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        setRecentLinks([]);
        const message = error instanceof Error ? error.message : t('overview.errorLinks');
        setNotice({ type: 'error', message });
      } finally {
        if (active) setRecentLinksLoading(false);
      }
    };
    loadRecentLinks();
    return () => { active = false; controller.abort(); };
  }, [selectedTenant?.tenantId, setNotice, token]);

  const applyAnalyticsQuery = async () => {
    const preset = WINDOW_PRESETS[windowPreset];
    try {
      setAnalyticsLoading(true);
      await refreshAnalytics({
        days: preset.days,
        granularity: preset.granularity,
        urlCode: null,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : t('overview.errorAnalytics');
      setNotice({ type: 'error', message });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  if (!selectedTenant) {
    return (
      <Card className="flex flex-col items-center justify-center p-10 text-center">
        <CardHeader>
          <CardTitle className="text-lg">{t('common.noWorkspace')}</CardTitle>
          <CardDescription>{t('overview.noWorkspaceDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild><Link to="/workspaces">{t('overview.openAccount')}</Link></Button>
        </CardContent>
      </Card>
    );
  }

  const hasAnalyticsData = analytics && analytics.status.state === 'ready' && !analytics.selectedUrlCode;
  const series = hasAnalyticsData ? analytics.series : [];
  const maxSeriesClicks = Math.max(1, ...series.map((p) => p.clicks));

  const totalClicks = hasAnalyticsData ? analytics.summary.totalClicks : 0;
  const uniqueIps = hasAnalyticsData ? analytics.summary.uniqueIps : 0;

  return (
    <div className="space-y-4 sm:space-y-5">
      <Card className="border-primary/15">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <Tabs value={windowPreset} onValueChange={(v) => setWindowPreset(v as WindowPreset)}>
              <TabsList className="w-full justify-start overflow-x-auto sm:w-auto">
                <TabsTrigger value="24h">{t('overview.window24h')}</TabsTrigger>
                <TabsTrigger value="7d">{t('overview.window7d')}</TabsTrigger>
                <TabsTrigger value="30d">{t('overview.window30d')}</TabsTrigger>
                <TabsTrigger value="90d">{t('overview.window90d')}</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button onClick={applyAnalyticsQuery} disabled={analyticsLoading} className="w-full min-w-[100px] sm:w-auto">
              {analyticsLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('overview.analyzing')}
                </>
              ) : (
                t('overview.analyze')
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {analyticsLoading && !hasAnalyticsData && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Card key={i}>
                <CardContent className="p-4 space-y-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-7 w-16" />
                </CardContent>
              </Card>
            ))}
          </div>
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-56 w-full rounded-lg" />
            </CardContent>
          </Card>
        </div>
      )}

      {!analyticsLoading && analytics?.status.state === 'empty' && (
        <Card>
          <CardContent className="py-14 text-center">
            <BarChart3 className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm font-medium text-muted-foreground">{t('overview.noDataTitle')}</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              {t('overview.noDataDesc')}
            </p>
          </CardContent>
        </Card>
      )}

      {!analyticsLoading && !hasAnalyticsData && analytics?.status.state !== 'empty' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Card key={i}>
                <CardContent className="p-4 space-y-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-7 w-16" />
                </CardContent>
              </Card>
            ))}
          </div>
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-56 w-full rounded-lg" />
            </CardContent>
          </Card>
        </div>
      )}

      {hasAnalyticsData && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card className="border-primary/10 bg-gradient-to-br from-card to-cyan-50/40">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('overview.totalClicks')}</p>
                <p className="text-2xl font-bold tabular-nums mt-1">{formatCount(totalClicks)}</p>
              </CardContent>
            </Card>
            <Card className="border-primary/10 bg-gradient-to-br from-card to-cyan-50/40">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('overview.uniqueIPs')}</p>
                <p className="text-2xl font-bold tabular-nums mt-1">{formatCount(uniqueIps)}</p>
              </CardContent>
            </Card>
            <Card className="border-primary/10 bg-gradient-to-br from-card to-cyan-50/40">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('overview.botRate')}</p>
                <p className="text-2xl font-bold tabular-nums mt-1">{analytics.botSummary.botPercentage.toFixed(1)}%</p>
              </CardContent>
            </Card>
            <Card className="border-primary/10 bg-gradient-to-br from-card to-cyan-50/40">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('overview.humanClicks')}</p>
                <p className="text-2xl font-bold tabular-nums mt-1">{formatCount(analytics.botSummary.humanClicks)}</p>
              </CardContent>
            </Card>
          </div>

          {series.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold">{t('overview.chartTitle')}</CardTitle>
                    {analytics.generatedAt && (
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {t('overview.aggregatesUpdated', { date: formatDate(analytics.generatedAt) })}
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="flex">
                  {/* Y axis */}
                  <div className="w-10 shrink-0 flex flex-col justify-between pr-2 text-right" style={{ height: 140 }}>
                    <span className="text-[10px] text-muted-foreground tabular-nums leading-none">{formatCount(maxSeriesClicks)}</span>
                    <span className="text-[10px] text-muted-foreground tabular-nums leading-none">{formatCount(Math.round(maxSeriesClicks / 2))}</span>
                    <span className="text-[10px] text-muted-foreground tabular-nums leading-none">0</span>
                  </div>
                  {/* Bars */}
                  <div className="flex-1 relative" style={{ height: 140 }}>
                    {/* Grid lines */}
                    <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                      <div className="border-t border-dashed border-muted/60" />
                      <div className="border-t border-dashed border-muted/60" />
                      <div className="border-t border-muted/60" />
                    </div>
                    <div className="flex items-end gap-[2px] relative" style={{ height: '100%' }}>
                      {series.map((point, i) => {
                        const heightPct = maxSeriesClicks > 0 ? (point.clicks / maxSeriesClicks) * 100 : 0;
                        return (
                          <div
                            key={`${point.bucketStart}-${i}`}
                            className="flex-1 group relative flex flex-col justify-end"
                            style={{ height: '100%' }}
                          >
                            <div
                              className="w-full bg-primary/35 rounded-t-sm hover:bg-primary/55 transition-colors min-h-[1px]"
                              style={{ height: `${Math.max(heightPct, 0.5)}%` }}
                            />
                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-foreground text-background text-[10px] px-2 py-1 rounded whitespace-nowrap z-10 shadow-sm">
                              <strong>{formatCount(point.clicks)}</strong> click
                              <br />
                              <span className="opacity-75">{formatDate(point.bucketStart)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {/* X axis labels */}
                <div className="flex items-center justify-between mt-1.5 ml-10 text-[10px] text-muted-foreground">
                  <span>{formatDate(series[0].bucketStart)}</span>
                  <span>{formatDate(series[series.length - 1].bucketStart)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-3">{analytics.status.logsDelayHint}</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-base font-semibold">{t('overview.recentLinks')}</CardTitle>
              <CardDescription className="text-xs">{t('overview.recentLinksDesc')}</CardDescription>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/links">{t('overview.manage')}</Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {recentLinksLoading ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('overview.colCode')}</TableHead>
                  <TableHead>{t('overview.colShortUrl')}</TableHead>
                  <TableHead className="text-right">{t('overview.colClicks')}</TableHead>
                  <TableHead>{t('overview.colCreated')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 5 }, (_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-36" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-4 w-10 ml-auto" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : recentLinks.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">{t('overview.noLinks')}</p>
          ) : (
            <>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('overview.colCode')}</TableHead>
                      <TableHead>{t('overview.colShortUrl')}</TableHead>
                      <TableHead>{t('overview.colTarget', 'Target')}</TableHead>
                      <TableHead>{t('overview.colCreated')}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentLinks.map((link) => {
                      const shortUrl = makeShortUrl(link.urlCode, link.domain);
                      return (
                        <TableRow key={link.urlCode}>
                          <TableCell><code className="rounded bg-muted px-1.5 py-0.5 text-sm">{link.urlCode}</code></TableCell>
                          <TableCell>
                            {shortUrl ? (
                              <a href={shortUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4 hover:text-primary/80">
                                {shortUrl}
                              </a>
                            ) : (
                              <span className="text-xs text-muted-foreground">{t('overview.configureBaseUrl')}</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[300px] truncate">
                            <a href={link.originalUrl} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline">{link.originalUrl}</a>
                          </TableCell>
                          <TableCell>{formatDate(link.createdAt)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (shortUrl) navigator.clipboard.writeText(shortUrl);
                                }}
                              >
                                <Copy className="mr-1 h-3.5 w-3.5" />
                                {t('links.copy', 'Copy')}
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a
                                  href={shortUrl || '#'}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={!shortUrl ? 'opacity-50 pointer-events-none' : ''}
                                >
                                  <ExternalLink className="mr-1 h-3.5 w-3.5" />
                                  {t('common.open', 'Open')}
                                </a>
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <Link to={`/links/${link.urlCode}${link.domainId ? `?domainId=${encodeURIComponent(link.domainId)}` : ''}`}>
                                  <ArrowRight className="mr-1 h-3.5 w-3.5" />
                                  {t('links.detail', 'Detail')}
                                </Link>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="space-y-3 md:hidden">
                {recentLinks.map((link) => {
                  const shortUrl = makeShortUrl(link.urlCode, link.domain);
                  return (
                    <div key={link.urlCode} className="rounded-lg border p-3">
                      <div className="flex items-center justify-between">
                        <code className="rounded bg-muted px-1.5 py-0.5 text-sm">{link.urlCode}</code>
                        <span className="text-xs text-muted-foreground">{formatDate(link.createdAt)}</span>
                      </div>
                      <p className="mt-2 break-all text-sm">
                        {shortUrl ? (
                          <a href={shortUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4 hover:text-primary/80">
                            {shortUrl}
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('overview.configureBaseUrl')}</span>
                        )}
                      </p>
                      <p className="mt-1 truncate text-xs text-muted-foreground">{link.originalUrl}</p>
                      <div className="mt-2 flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => { if (shortUrl) navigator.clipboard.writeText(shortUrl); }}>
                          <Copy className="mr-1 h-3.5 w-3.5" />{t('links.copy', 'Copy')}
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/links/${link.urlCode}${link.domainId ? `?domainId=${encodeURIComponent(link.domainId)}` : ''}`}>
                            <ArrowRight className="mr-1 h-3.5 w-3.5" />{t('links.detail', 'Detail')}
                          </Link>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
